import {isValid} from 'date-fns';
import type {FlagProps} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import type {MonthlyStockTakeDetailResponse} from '@dms/api/reporting';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type Nullish, parseDate} from 'shared/web';

import type {MonthlyStockTakeMetric} from '../components/MonthlyStockTakeSection';
import type {StockTakeReportSection} from '../types/StockTakeReport';

const MONEY_DECIMALS = 2;
const COUNT_DECIMALS = 0;
const DECIMAL_DECIMALS = 2;

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function useMonthlyStockTakeDetailView(props: {
  detail: NonNullable<MonthlyStockTakeDetailResponse>;
  currency: string | Nullish;
}) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();

  const money = (value: number | Nullish) =>
    isNil(value) || isNil(props.currency)
      ? EMPTY_PLACEHOLDER
      : (formatCurrency(value, props.currency, MONEY_DECIMALS) ?? EMPTY_PLACEHOLDER);

  const count = (value: number | Nullish) =>
    formatNumber(value, COUNT_DECIMALS) ?? EMPTY_PLACEHOLDER;

  const decimal = (value: number | Nullish) =>
    formatNumber(value, DECIMAL_DECIMALS) ?? EMPTY_PLACEHOLDER;

  const percent = (value: number | Nullish) =>
    isNil(value) ? EMPTY_PLACEHOLDER : `${decimal(value)} %`;

  const date = (value: string | Nullish) => {
    const parsed = isNotNil(value) ? parseDate(value) : null;
    return isNotNil(parsed) && isValid(parsed)
      ? formatDateTime('dateShort', parsed)
      : EMPTY_PLACEHOLDER;
  };

  const booleanLabel = (value: boolean | Nullish) =>
    match(value)
      .with(true, () => i18n.t('general.labels.yes'))
      .with(false, () => i18n.t('general.labels.no'))
      .otherwise(() => EMPTY_PLACEHOLDER);

  const check = match<
    boolean | Nullish,
    {colorScheme: FlagProps['colorScheme']; label: string} | null
  >(props.detail.checkIsCorrect)
    .with(true, () => ({
      colorScheme: 'green' as const,
      label: i18n.t('entity.warehouse.reporting.checkMatches'),
    }))
    .with(false, () => ({
      colorScheme: 'red' as const,
      label: i18n.t('entity.warehouse.reporting.checkDoesNotMatch'),
    }))
    .otherwise(() => null);

  const documents = (value: number | Nullish) =>
    i18n.t('entity.warehouse.reporting.documentsLine', {count: count(value)});

  const articles = (value: number | Nullish) =>
    i18n.t('entity.warehouse.reporting.articlesLine', {count: count(value)});

  const summaryMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'openingInventoryValue',
      label: i18n.t('entity.warehouse.reporting.openingValue'),
      value: money(props.detail.openingInventoryValue),
      note: `${i18n.t('entity.warehouse.reporting.cardsWithValue')}: ${count(
        props.detail.articlesWithValueStart
      )}`,
    },
    {
      id: 'closingInventoryValue',
      label: i18n.t('entity.warehouse.reporting.closingValue'),
      value: money(props.detail.closingInventoryValue),
      note: `${i18n.t('entity.warehouse.reporting.cardsWithValue')}: ${count(
        props.detail.articlesWithValueEnd
      )}`,
    },
    {
      id: 'inventoryValueDifference',
      label: i18n.t('entity.warehouse.reporting.inventoryDifference'),
      value: money(props.detail.inventoryValueDifference),
      note: percent(props.detail.momDifferencePercent),
    },
    {
      id: 'checkSum',
      label: i18n.t('entity.warehouse.reporting.checkTotal'),
      value: money(props.detail.checkSum),
      tooltip: i18n.t('entity.warehouse.tooltips.monthlyCheckTotal'),
      note: check?.label ?? EMPTY_PLACEHOLDER,
    },
  ];

  const monthlyActivityMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'monthlyReceiptsValue',
      label: i18n.t('entity.warehouse.reporting.monthlyReceipts'),
      value: money(props.detail.monthlyReceiptsValue),
      note: articles(props.detail.receiptedArticlesCount),
    },
    {
      id: 'monthlyIssuesValue',
      label: i18n.t('entity.warehouse.reporting.monthlyIssues'),
      value: money(props.detail.monthlyIssuesValue),
      note: articles(props.detail.issuedArticlesCount),
    },
    {
      id: 'monthlyCorrectionsValue',
      label: i18n.t('entity.warehouse.reporting.correctionsAndAdjustments'),
      value: money(props.detail.monthlyCorrectionsValue),
    },
  ];

  const validationMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'openingSourceDay',
      label: i18n.t('entity.warehouse.reporting.openingSourceDay'),
      value: date(props.detail.openingSourceDay),
    },
    {
      id: 'isContinuityOk',
      label: i18n.t('entity.warehouse.reporting.continuityStatus'),
      value: booleanLabel(props.detail.isContinuityOk),
    },
    {
      id: 'continuityDifference',
      label: i18n.t('entity.warehouse.reporting.continuityDifference'),
      value: money(props.detail.continuityDifference),
    },
  ];

  const comparisonMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'momDifferenceValue',
      label: i18n.t('entity.warehouse.reporting.inventoryDifference'),
      value: money(props.detail.momDifferenceValue),
    },
    {
      id: 'momDifferencePercent',
      label: i18n.t('entity.warehouse.reporting.percentageDifference'),
      value: percent(props.detail.momDifferencePercent),
    },
  ];

  const completedReceiptMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'receiptsFromSuppliers',
      label: i18n.t('entity.warehouse.reporting.receiptsFromSuppliers'),
      value: money(props.detail.receiptsFromSuppliers),
      note: documents(props.detail.receiptsFromSuppliersCount),
    },
    {
      id: 'transferToWarehouse',
      label: i18n.t('entity.warehouse.reporting.monthlyTransfersToWarehouse'),
      value: money(props.detail.transferToWarehouse),
      note: documents(props.detail.transferToWarehouseCount),
    },
    {
      id: 'materialReturns',
      label: i18n.t('entity.warehouse.reporting.materialReturns'),
      value: money(props.detail.materialReturns),
      note: documents(props.detail.materialReturnsCount),
    },
  ];

  const completedIssueMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'issuancesToOrders',
      label: i18n.t('entity.warehouse.reporting.issuesToOrders'),
      value: money(props.detail.issuancesToOrders),
      note: documents(props.detail.issueNotesToOrdersCount),
    },
    {
      id: 'transferFromWarehouse',
      label: i18n.t('entity.warehouse.reporting.monthlyTransfersFromWarehouse'),
      value: money(props.detail.transferFromWarehouse),
      note: documents(props.detail.transferFromWarehouseCount),
    },
  ];

  const inProgressMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'receiptNoteInProgress',
      label: i18n.t('entity.warehouse.reporting.inProgressReceipts'),
      value: money(props.detail.receiptNoteInProgress),
    },
    {
      id: 'pendingGoodsPurchase',
      label: i18n.t('entity.warehouse.reporting.pendingGoodsPurchase'),
      value: money(props.detail.pendingGoodsPurchase),
    },
    {
      id: 'pendingGoodsTransfer',
      label: i18n.t('entity.warehouse.reporting.pendingGoodsTransfer'),
      value: money(props.detail.pendingGoodsTransfer),
    },
  ];

  const pendingReliabilityMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'pendingIsReliable',
      label: i18n.t('general.labels.status'),
      value: booleanLabel(props.detail.pendingIsReliable),
    },
  ];

  const healthMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'averageInventoryValue',
      label: i18n.t('entity.warehouse.reporting.averageInventoryValue'),
      value: money(props.detail.averageInventoryValue),
    },
    {
      id: 'inventoryTurnover',
      label: i18n.t('entity.warehouse.reporting.inventoryTurnover'),
      value: decimal(props.detail.inventoryTurnover),
    },
    {
      id: 'inventoryDays',
      label: i18n.t('entity.warehouse.reporting.inventoryDays'),
      value: decimal(props.detail.inventoryDays),
    },
    {
      id: 'averageArticleValue',
      label: i18n.t('entity.warehouse.reporting.averageArticleValue'),
      value: money(props.detail.averageArticleValue),
    },
  ];

  const slowStockMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'slowStock3mValue',
      label: i18n.t('entity.warehouse.reporting.slowStock3m'),
      value: money(props.detail.slowStock_3mValue),
    },
    {
      id: 'slowStock6mValue',
      label: i18n.t('entity.warehouse.reporting.slowStock6m'),
      value: money(props.detail.slowStock_6mValue),
    },
    {
      id: 'slowStock12mValue',
      label: i18n.t('entity.warehouse.reporting.slowStock12m'),
      value: money(props.detail.slowStock_12mValue),
    },
    {
      id: 'slowStock6mShare',
      label: i18n.t('entity.warehouse.reporting.slowStock6mShare'),
      value: percent(props.detail.slowStock_6mShare),
    },
  ];

  const slowStockCountMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'slowStock3mArticleCount',
      label: i18n.t('entity.warehouse.reporting.slowStock3m'),
      value: count(props.detail.slowStock_3mArticleCount),
    },
    {
      id: 'slowStock6mArticleCount',
      label: i18n.t('entity.warehouse.reporting.slowStock6m'),
      value: count(props.detail.slowStock_6mArticleCount),
    },
    {
      id: 'slowStock12mArticleCount',
      label: i18n.t('entity.warehouse.reporting.slowStock12m'),
      value: count(props.detail.slowStock_12mArticleCount),
    },
  ];

  const ytdMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'receiptsYtdValue',
      label: i18n.t('entity.warehouse.reporting.receiptsYtd'),
      value: money(props.detail.receiptsYtdValue),
    },
    {
      id: 'issuesYtdValue',
      label: i18n.t('entity.warehouse.reporting.issuesYtd'),
      value: money(props.detail.issuesYtdValue),
    },
  ];

  const inventoryMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'inventoryAdjustmentValue',
      label: i18n.t('entity.warehouse.reporting.inventoryAdjustmentValue'),
      value: money(props.detail.inventoryAdjustmentValue),
    },
    {
      id: 'inventoryDocCount',
      label: i18n.t('entity.warehouse.reporting.inventoryDocuments'),
      value: count(props.detail.inventoryDocCount),
    },
    {
      id: 'inventoryArticleCount',
      label: i18n.t('entity.warehouse.reporting.inventoryArticles'),
      value: count(props.detail.inventoryArticleCount),
    },
  ];

  const writeOffMetrics: MonthlyStockTakeMetric[] = [
    {
      id: 'writeOffValue',
      label: i18n.t('entity.warehouse.reporting.writeOffValue'),
      value: money(props.detail.writeOffValue),
    },
    {
      id: 'writeOffDocCount',
      label: i18n.t('entity.warehouse.reporting.writeOffDocuments'),
      value: count(props.detail.writeOffDocCount),
    },
  ];

  const sections: StockTakeReportSection[] = [
    {
      id: 'summary',
      title: i18n.t('general.labels.summary'),
      groups: [
        {
          id: 'inventoryValues',
          title: i18n.t('entity.warehouse.reporting.inventoryValues'),
          metrics: summaryMetrics,
        },
        {
          id: 'monthlyActivity',
          title: i18n.t('entity.warehouse.reporting.monthlyActivity'),
          metrics: monthlyActivityMetrics,
        },
        {
          id: 'validationAndContinuity',
          title: i18n.t('entity.warehouse.reporting.validationAndContinuity'),
          metrics: validationMetrics,
        },
        {
          id: 'changeFromPreviousMonth',
          title: i18n.t('entity.warehouse.reporting.changeFromPreviousMonth'),
          metrics: comparisonMetrics,
        },
      ],
    },
    {
      id: 'completed',
      title: i18n.t('entity.warehouse.reporting.completedMovements'),
      groups: [
        {
          id: 'receiptsBreakdown',
          title: i18n.t('entity.warehouse.reporting.receiptsBreakdown'),
          metrics: completedReceiptMetrics,
        },
        {
          id: 'issuesBreakdown',
          title: i18n.t('entity.warehouse.reporting.issuesBreakdown'),
          metrics: completedIssueMetrics,
        },
        {
          id: 'monthlyActivity',
          title: i18n.t('entity.warehouse.reporting.monthlyActivity'),
          metrics: monthlyActivityMetrics,
        },
      ],
    },
    {
      id: 'inProgress',
      title: i18n.t('entity.warehouse.reporting.inProgressMovements'),
      groups: [
        {
          id: 'inProgressMovements',
          title: i18n.t('entity.warehouse.reporting.inProgressMovements'),
          metrics: inProgressMetrics,
        },
        {
          id: 'pendingSnapshotReliability',
          title: i18n.t('entity.warehouse.reporting.pendingSnapshotReliability'),
          metrics: pendingReliabilityMetrics,
        },
      ],
    },
    {
      id: 'health',
      title: i18n.t('entity.warehouse.reporting.inventoryHealth'),
      groups: [
        {
          id: 'inventoryHealth',
          title: i18n.t('entity.warehouse.reporting.inventoryHealth'),
          metrics: healthMetrics,
        },
        {
          id: 'slowStock',
          title: i18n.t('entity.warehouse.reporting.slowStock'),
          metrics: slowStockMetrics,
        },
        {
          id: 'cardsWithValue',
          title: i18n.t('entity.warehouse.reporting.cardsWithValue'),
          metrics: slowStockCountMetrics,
        },
      ],
    },
    {
      id: 'inventory',
      title: i18n.t('entity.warehouse.reporting.inventoryAndYtd'),
      groups: [
        {
          id: 'yearToDate',
          title: i18n.t('entity.warehouse.reporting.yearToDate'),
          metrics: ytdMetrics,
        },
        {
          id: 'inventoryAdjustments',
          title: i18n.t('entity.warehouse.reporting.inventoryAdjustments'),
          metrics: inventoryMetrics,
        },
        {
          id: 'writeOffs',
          title: i18n.t('entity.warehouse.reporting.writeOffs'),
          metrics: writeOffMetrics,
        },
      ],
    },
  ];

  return {
    check,
    date,
    money,
    sections,
    summaryMetrics,
    monthlyActivityMetrics,
    validationMetrics,
    comparisonMetrics,
    completedReceiptMetrics,
    completedIssueMetrics,
    inProgressMetrics,
    pendingReliabilityMetrics,
    healthMetrics,
    slowStockMetrics,
    slowStockCountMetrics,
    ytdMetrics,
    inventoryMetrics,
    writeOffMetrics,
  };
}
